/**
 * L'EN-TÊTE DU SITE — refonte « bonnes affaires » du 2026-09-14.
 *
 * ZENKART = LES BONNES AFFAIRES. L'en-tête le dit sur chaque page : le logo
 * mène à la vitrine, la recherche mène à /explore, le premier lien est
 * « Bonnes affaires ». Rien d'autre ne demande l'attention.
 *
 * Même nom et même hauteur fixe (64 px) que la barre précédente : les seize
 * pages qui l'importent la reçoivent sans changer leur marge haute.
 *
 *   visiteur   logo · recherche · Bonnes affaires, Vendre, Trouver, Tarifs · Connexion
 *   abonné     logo · recherche · Bonnes affaires, Vendre, Trouver · menu du compte
 *   mobile     logo · recherche · menu ; barre d'onglets en bas pour l'abonné
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import ZenkartBrand from './ZenkartBrand';
import './Navbar.css';

function Loupe() {
  return (
    <svg width="15" height="15" viewBox="0 0 22 22" aria-hidden="true">
      <circle cx="9.5" cy="9.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
      <path d="M14.5 14.5L20 20" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

const ICONES = {
  affaires: <path d="M3 9.5 9.5 3H15v5.5L8.5 15 3 9.5Zm9-3.5h.01" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />,
  favoris: <path d="M9 15s-6-3.6-6-8a3.4 3.4 0 0 1 6-2.2A3.4 3.4 0 0 1 15 7c0 4.4-6 8-6 8Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />,
  alertes: <path d="M9 2a5 5 0 0 0-5 5v3l-1.5 2.5h13L14 10V7a5 5 0 0 0-5-5Zm-1.5 12.5a1.5 1.5 0 0 0 3 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />,
  compte: <path d="M9 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-6 7a6 6 0 0 1 12 0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />,
};

function Icone({ nom }) {
  return <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">{ICONES[nom]}</svg>;
}

export default function Navbar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [recherche, setRecherche] = useState('');
  const [menu, setMenu] = useState(false);
  const [nonLues, setNonLues] = useState(0);
  const minuteur = useRef(null);

  useEffect(() => { setMenu(false); }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/explore') {
      setRecherche(new URLSearchParams(location.search).get('q') || '');
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!user) return undefined;
    const lire = () => api.get('/api/alerts/unread-count')
      .then((r) => setNonLues(r.data.count || 0))
      .catch(() => {});
    lire();
    minuteur.current = setInterval(lire, 60000);
    return () => clearInterval(minuteur.current);
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/alerts' && nonLues > 0) {
      api.post('/api/alerts/mark-read').then(() => setNonLues(0)).catch(() => {});
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!menu) return undefined;
    const echap = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('keydown', echap);
    return () => document.removeEventListener('keydown', echap);
  }, [menu]);

  function chercher(e) {
    e.preventDefault();
    const texte = recherche.trim();
    navigate(texte ? `/explore?q=${encodeURIComponent(texte)}` : '/explore');
  }

  function quitter() {
    setMenu(false);
    signOut();
    navigate('/');
  }

  const badge = nonLues > 99 ? '99+' : nonLues;
  const liensCompte = user ? [
    { to: '/dashboard', l: 'Tableau de bord' },
    { to: '/favoris', l: 'Favoris' },
    { to: '/alerts', l: 'Alertes', badge: true },
    { to: '/historique', l: 'Historique' },
    { to: '/profile', l: 'Profil' },
    /* Plus de « Tarifs », d'« Affiliation » ni de « Garage » (Garage Pass)
       depuis le 2026-10-22 : ZENKART ne vend plus d'abonnement. */
    /* Réservé aux garages partenaires : le serveur exige SIRET vérifié ET
       validation manuelle, donc on n'affiche le lien que si les deux sont là. */
    ...(user.siret_verifie && user.valide_par_admin
      ? [{ to: '/garage/depots', l: 'Véhicules à vendre' }] : []),
    ...(user.is_admin ? [{ to: '/admin', l: 'Administration' }] : []),
  ] : [];

  return (
    <>
      <header className="zk-nav">
        <div className="zk-nav-in">
          <Link to="/" className="zk-nav-logo" aria-label="ZENKART, les bonnes affaires">
            <ZenkartBrand size="md" />
          </Link>

          <form className="zk-nav-recherche" role="search" onSubmit={chercher}>
            <label htmlFor="zk-nav-q" className="zk-nav-sr">Rechercher une bonne affaire</label>
            <Loupe />
            <input
              id="zk-nav-q"
              type="search"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Marque, modèle…"
              enterKeyHint="search"
              autoComplete="off"
            />
          </form>

          <nav className="zk-nav-liens" aria-label="Navigation principale">
            <NavLink to="/explore">Bonnes affaires</NavLink>
            <NavLink to="/vendre">Vendre ma voiture</NavLink>
            <NavLink to="/trouver">Trouvez ma voiture</NavLink>
          </nav>

          {user ? (
            <button
              type="button"
              className="zk-nav-compte"
              aria-expanded={menu}
              aria-controls="zk-nav-menu"
              onClick={() => setMenu((m) => !m)}
            >
              <span className="zk-nav-avatar" aria-hidden="true">{(user.username || '?').slice(0, 1).toUpperCase()}</span>
              <span className="zk-nav-nom">{user.username}</span>
              {nonLues > 0 && <span className="zk-nav-badge" aria-label={`${nonLues} alertes non lues`}>{badge}</span>}
            </button>
          ) : (
            <Link to="/login" className="zk-nav-connexion">Connexion</Link>
          )}

          <button
            type="button"
            className="zk-nav-burger"
            aria-label={menu ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={menu}
            aria-controls="zk-nav-menu"
            onClick={() => setMenu((m) => !m)}
          >
            <svg width="22" height="16" viewBox="0 0 22 16" aria-hidden="true">
              {menu
                ? <path d="M4 1l14 14M18 1 4 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                : <path d="M1 1h20M1 8h20M1 15h20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
            </svg>
            {user && nonLues > 0 && !menu && <span className="zk-nav-point" aria-hidden="true" />}
          </button>
        </div>
      </header>

      {menu && (
        <div className="zk-nav-voile" onClick={() => setMenu(false)}>
          <nav id="zk-nav-menu" className="zk-nav-menu" aria-label="Menu" onClick={(e) => e.stopPropagation()}>
            {user && (
              <p className="zk-nav-qui">
                {user.username}
              </p>
            )}
            <div className="zk-nav-mobile">
              <NavLink to="/explore">Bonnes affaires</NavLink>
              <NavLink to="/vendre">Vendre ma voiture</NavLink>
              <NavLink to="/trouver">Trouvez ma voiture</NavLink>
            </div>
            {user ? liensCompte.map((x) => (
              <NavLink key={x.to} to={x.to}>
                {x.l}
                {x.badge && nonLues > 0 && <span className="zk-nav-badge">{badge}</span>}
              </NavLink>
            )) : (
              <>
                <NavLink to="/login" className="zk-nav-fort">Connexion</NavLink>
                <NavLink to="/register">Créer un compte</NavLink>
              </>
            )}
            <hr />
            <NavLink to="/contact">Contact</NavLink>
            {user && <button type="button" onClick={quitter}>Se déconnecter</button>}
          </nav>
        </div>
      )}

      {user && (
        <nav className="zk-nav-onglets" aria-label="Raccourcis">
          {[
            { to: '/explore', l: 'Affaires', i: 'affaires' },
            { to: '/favoris', l: 'Favoris', i: 'favoris' },
            { to: '/alerts', l: 'Alertes', i: 'alertes' },
            { to: '/dashboard', l: 'Compte', i: 'compte' },
          ].map((o) => (
            <NavLink key={o.to} to={o.to}>
              <Icone nom={o.i} />
              {o.l}
              {o.to === '/alerts' && nonLues > 0 && <span className="zk-nav-badge">{nonLues > 9 ? '9+' : nonLues}</span>}
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}
